/**
 * Print Focus chapter — Canva page-03 "This month's focus".
 * Strategic direction, hero, mantra, window list, expect rows, insight.
 */

import React from "react";
import { Book, ChevronsRight, Eye, TrendingUp, User } from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import focusBackground from "../../../../assets/monthly-consultation/pdf/focus-background.png";
import {
  PdfChapterShell,
  PdfInsightStrip,
  PdfListItem,
} from "../atoms";
import {
  clipPdfText,
  executiveInsightLine,
  expectCardCopy,
  focusMantraCopy,
  windowToLines,
} from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardShadow,
  cardWhite,
  charcoal,
  displayFontFamily,
  mutedGray,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF Focus chapter.
 */
export interface PdfChapterFocusProps {
  /** Full monthly consultation payload. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/**
 * Resolve the webpack URL for the Focus calendar hero band.
 *
 * Uses user-supplied `focus-background.png` (wide desk-calendar photo) in the
 * same rounded-band role as Canva page-03 — not a full-page background.
 *
 * @returns Image URL, or empty string when the module did not yield a path
 */
function focusHeroSrc(): string {
  return typeof focusBackground === "string" ? focusBackground.trim() : "";
}

/** Decorative icons for the three "what to expect" rows (Book / User / TrendingUp). */
const EXPECT_ICONS = [Book, User, TrendingUp] as const;

/**
 * Collect non-empty string rows from a bundle array field.
 *
 * @param value - Unknown array candidate
 * @returns Trimmed strings
 */
function stringRows(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/**
 * This month's focus — one A4 sheet of strategic direction.
 */
export const PdfChapterFocus: React.FC<PdfChapterFocusProps> = ({
  bundle,
  pageNumber,
}) => {
  const headline =
    typeof bundle.headline === "string" ? bundle.headline.trim() : "";
  const identityLine =
    typeof bundle.archetype.identityLine === "string"
      ? bundle.archetype.identityLine.trim()
      : "";
  const primaryGoal =
    typeof bundle.briefing.primaryGoal === "string"
      ? bundle.briefing.primaryGoal.trim()
      : "";
  const priority =
    typeof bundle.briefing.priority === "string"
      ? bundle.briefing.priority.trim()
      : "";
  const usualAsk =
    typeof bundle.rarity.usualAsk === "string"
      ? bundle.rarity.usualAsk.trim()
      : "";

  const mantra = focusMantraCopy({
    headline,
    identityLine,
    primaryGoal,
    usualAsk,
  });
  const windowLines = windowToLines(
    stringRows(bundle.rarity.usualAskLines),
    stringRows(bundle.rarity.actionSteps),
    3
  );
  const expectLines = stringRows(bundle.archetype.signatureBehaviors).slice(0, 3);
  const insight = executiveInsightLine(priority, headline);
  const hero = focusHeroSrc();

  return (
    <PdfChapterShell
      chapterId="focus"
      eyebrow="Strategic direction"
      title="This month's focus"
      pageNumber={pageNumber}
      compact
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          flex: "1 1 auto",
          minHeight: 0,
          fontFamily: bodyFontFamily,
        }}
      >
        {hero.length > 0 ? (
          <img
            src={hero}
            alt=""
            className="mc-pdf-hero-img mc-pdf-hero-img--focus"
            style={{ flexShrink: 0 }}
          />
        ) : null}

        {mantra.heading.length > 0 ? (
          <div
            className="mc-pdf-card"
            style={{
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
              background: cardWhite,
              border: `1.5px solid ${terracotta}`,
              borderRadius: 16,
              boxShadow: cardShadow,
              padding: "14px 16px",
              boxSizing: "border-box",
              flexShrink: 0,
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: `${terracotta}18`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ChevronsRight size={20} color={terracotta} strokeWidth={2.4} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: displayFontFamily,
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.35,
                  color: charcoal,
                }}
              >
                {mantra.heading}
              </p>
              {mantra.body.length > 0 ? (
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: mutedGray,
                  }}
                >
                  {clipPdfText(mantra.body, 280)}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: mutedGray }}>
            No focus mantra is available for this month.
          </p>
        )}

        <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          <p
            className="mc-pdf-section-rule"
            style={{
              margin: "0 0 10px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: terracotta,
              flexShrink: 0,
            }}
          >
            This is the window to:
          </p>
          {windowLines.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                flex: "1 1 auto",
              }}
            >
              {windowLines.map((line, index) => (
                <div
                  key={`${String(index)}-${line.slice(0, 24)}`}
                  className="mc-pdf-card"
                  style={{
                    background: cardWhite,
                    border: `1px solid ${border}`,
                    borderRadius: 14,
                    padding: "12px 14px",
                    boxSizing: "border-box",
                    flex: "1 1 auto",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <PdfListItem index={index + 1} title={line} />
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: mutedGray }}>
              No window items are listed for this month.
            </p>
          )}
        </div>

        <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          <p
            className="mc-pdf-section-rule"
            style={{
              margin: "0 0 10px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: terracotta,
              flexShrink: 0,
            }}
          >
            <Eye size={15} color={terracotta} strokeWidth={2} aria-hidden="true" />
            What to expect this month
          </p>
          {expectLines.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                flex: "1 1 auto",
              }}
            >
              {expectLines.map((line, index) => {
                const copy = expectCardCopy(line);
                const resolvedIcon = EXPECT_ICONS[index];
                const Icon = resolvedIcon !== undefined ? resolvedIcon : Book;
                const titleText =
                  copy.title.length > 0
                    ? `${String(index + 1)}. ${copy.title}`
                    : `${String(index + 1)}.`;
                return (
                  <div
                    key={`${String(index)}-${line.slice(0, 24)}`}
                    className="mc-pdf-card"
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      background: cardWhite,
                      border: `1px solid ${border}`,
                      borderRadius: 14,
                      padding: "12px 14px",
                      boxSizing: "border-box",
                      flex: "1 1 auto",
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: `${terracotta}18`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={16} color={terracotta} strokeWidth={2} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p
                        style={{
                          margin: 0,
                          fontFamily: displayFontFamily,
                          fontSize: 14,
                          fontWeight: 700,
                          lineHeight: 1.3,
                          color: terracotta,
                        }}
                      >
                        {titleText}
                      </p>
                      {copy.body.length > 0 ? (
                        <p
                          style={{
                            margin: "6px 0 0",
                            fontSize: 12,
                            lineHeight: 1.45,
                            color: mutedGray,
                          }}
                        >
                          {clipPdfText(copy.body, 220)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: 13, color: mutedGray }}>
              No signature behaviors are listed for this month.
            </p>
          )}
        </div>

        {insight.length > 0 ? (
          <div style={{ flexShrink: 0 }}>
            <PdfInsightStrip
              label="Executive insight"
              text={insight}
              variant="darkQuote"
            />
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: mutedGray }}>
            No executive insight is available for this month.
          </p>
        )}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterFocus;
